import React, { useReducer, createContext, Dispatch, useContext } from 'react'
import { EventStages, IUser } from 'types'

interface IAppContextObject {
  user: IUser | null
  postStream?: boolean
  showLivestream?: boolean
  eventStage?: EventStages | undefined
  isNavigating: boolean
  userInitiatedMute: boolean
  demoImagesLoaded: boolean
  sessionImagesLoaded: boolean
}

const initialState = {
  user: null,
  postStream: false,
  showLivestream: false,
  eventStage: undefined,
  isNavigating: false,
  userInitiatedMute: false,
  demoImagesLoaded: false,
  sessionImagesLoaded: false
}

export const AppContext = createContext<{
  appState: IAppContextObject
  dispatch: Dispatch<any>
  setUser: (payload: IUser | null) => void
  setShowLivestream: (payload: boolean) => void
  setAppEventStage: (payload: EventStages) => void
  setNavigating: (payload: boolean) => void
  setUserInitiatedMute: (payload: boolean) => void
  setDemoImagesLoaded: (payload: boolean) => void
  setSessionImagesLoaded: (payload: boolean) => void
}>({
  appState: initialState,
  dispatch: () => null,
  setUser: () => null,
  setShowLivestream: () => null,
  setAppEventStage: () => null,
  setNavigating: () => null,
  setUserInitiatedMute: () => null,
  setDemoImagesLoaded: () => null,
  setSessionImagesLoaded: () => null
})

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER':
      return {
        ...state,
        user: action.payload
      }
    case 'SET_POST_LIVE_STREAM':
      return {
        ...state,
        postStream: action.payload
      }
    case 'SET_SHOW_LIVESTREAM':
      return {
        ...state,
        showLivestream: action.payload
      }
    case 'SET_EVENT_STAGE':
      return {
        ...state,
        eventStage: action.payload
      }
    case 'SET_IS_NAVIGATING':
      return {
        ...state,
        isNavigating: action.payload
      }
    case 'USER_INITIATED_MUTE':
      return {
        ...state,
        userInitiatedMute: action.payload
      }
    case 'DEMO_IMAGES_LOADED':
      return {
        ...state,
        demoImagesLoaded: action.payload
      }
    case 'SESSION_IMAGES_LOADED':
      return {
        ...state,
        sessionImagesLoaded: action.payload
      }
    default:
      return state
  }
}

export const AppStateProvider = props => {
  const [appState, dispatch] = useReducer(reducer, initialState)

  const setUser = (payload: IUser | null) => {
    return dispatch({ type: 'SET_USER', payload })
  }
  // eslint-disable-next-line
  // const setPostLiveStream = (isPostLS: boolean) => {
  //   return dispatch({ type: 'SET_POST_LIVE_STREAM', payload: isPostLS })
  // }

  const setShowLivestream = (payload: boolean) => {
    return dispatch({ type: 'SET_SHOW_LIVESTREAM', payload })
  }

  const setAppEventStage = (payload: EventStages) => {
    return dispatch({ type: 'SET_EVENT_STAGE', payload })
  }

  const setNavigating = (payload: boolean) => {
    return dispatch({ type: 'SET_IS_NAVIGATING', payload })
  }

  const setUserInitiatedMute = (payload: boolean) => {
    return dispatch({ type: 'USER_INITIATED_MUTE', payload })
  }

  const setDemoImagesLoaded = (payload: boolean) => {
    return dispatch({ type: 'DEMO_IMAGES_LOADED', payload })
  }

  const setSessionImagesLoaded = (payload: boolean) => {
    return dispatch({ type: 'SESSION_IMAGES_LOADED', payload })
  }

  return (
    <AppContext.Provider
      value={{
        appState,
        dispatch,
        setUser,
        setShowLivestream,
        setAppEventStage,
        setNavigating,
        setUserInitiatedMute,
        setDemoImagesLoaded,
        setSessionImagesLoaded
      }}
    >
      {props.children}
    </AppContext.Provider>
  )
}

export const useAppState = () => useContext(AppContext)
